import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { map, pluck, tap, first, filter, switchMap } from 'rxjs/operators'

import { TaxCodeDatabase } from '../db/local-tax-code.db'
import { IApiTic } from '../models/iapi-tic'
import { ITaxCode } from '../models/itax-code.model'
import { TaxCodeAdapter } from '../utilities/tax-code-adapter'
import { LocalStorageNameSpaceEnum } from 'src/app/enums/local-storage.enums'
import moment from 'moment'

@Injectable({
  providedIn: 'root',
})
export class TaxCodeService {
  private db = new TaxCodeDatabase()
  private adapter = new TaxCodeAdapter()

  constructor(private http: HttpClient) {}

  public updateIfNecessary(): Observable<boolean> {
    return this.ticNeedsUpdating().pipe(
      filter((needsUpdate) => !!needsUpdate),
      switchMap((_) => this.getTicsFromApi()),
      map(() => true)
    )
  }

  public getTicsFromApi(): Observable<any> {
    return this.http
      .get('api/tics', {
        headers: new HttpHeaders({
          ContentType: 'application/json',
        }),
      })
      .pipe(
        pluck('result'),
        map((ticCodes: IApiTic[]) =>
          ticCodes.map((tic) => this.adapter.mapApiTicResultsToITaxCode(tic))
        ),
        tap((taxCodes) => this.db.saveTaxCodes(taxCodes)),
        tap((_) => this.saveLastUpdateToLocalStorage())
      )
  }

  getTopTaxCodes(): Observable<ITaxCode[]> {
    return this.db.getTopTaxCodes()
  }

  getTaxCodes() {
    return this.db.getTaxCodes()
  }

  getChildren(parentTic: number): Observable<ITaxCode[]> {
    return this.db.getChildren(parentTic)
  }

  getTaxCode(ticId: number): Observable<ITaxCode> {
    return this.db.getTaxCode(ticId)
  }

  private saveLastUpdateToLocalStorage(): void {
    localStorage.setItem(LocalStorageNameSpaceEnum.TicLastUpdate, moment().format())
  }

  private ticNeedsUpdating(): Observable<boolean> {
    try {
      const lastUpdate = localStorage.getItem(LocalStorageNameSpaceEnum.TicLastUpdate)
      if (!lastUpdate) {
        return of(true)
      }
      const lastUpdateDay = moment(lastUpdate)
      if (!lastUpdateDay.isValid) {
        return of(true)
      }

      if (moment().isAfter(lastUpdateDay.add(7, 'days'))) {
        return of(true)
      }

      return this.db.totalCodes().pipe(
        first(),
        map((totalNumber) => (totalNumber === 0 ? true : false))
      )
    } catch (e) {
      return of(true)
    }
  }
}
